import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone, ArrowUpRight } from "lucide-react";
import { NAV, SITE } from "../mock";

const Layout = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#0E0E0F] text-[#EDE3CB]">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          scrolled || open ? "bg-[#0E0E0F]/95 backdrop-blur border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex items-center justify-between h-[72px] gap-6">
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <div className="w-9 h-9 border border-[#B8935A] flex items-center justify-center">
              <span className="font-display text-[#B8935A] text-xl leading-none mt-0.5">JJA</span>
            </div>
            <div className="hidden xl:block leading-tight">
              <div className="font-display text-base tracking-wider text-[#EDE3CB] group-hover:text-[#B8935A] transition-colors whitespace-nowrap">JESSE J. ADAMS</div>
              <div className="text-[9px] tracking-[0.26em] uppercase text-[#B8935A]/80 -mt-0.5 whitespace-nowrap">Los Angeles Based Property Master · Local 44</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `text-[11px] tracking-[0.16em] uppercase font-medium link-underline whitespace-nowrap ${
                    isActive ? "text-[#B8935A]" : "text-[#EDE3CB]/85 hover:text-[#EDE3CB]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block shrink-0">
            <Link to="/contact" className="btn-primary !py-2.5 !px-4 !text-[11px] !tracking-[0.18em] whitespace-nowrap">
              Availability <ArrowUpRight size={12} />
            </Link>
          </div>

          <button
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-[#EDE3CB]"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden bg-[#0E0E0F] border-t border-white/5">
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  className={({ isActive }) =>
                    `text-sm tracking-[0.2em] uppercase ${isActive ? "text-[#B8935A]" : "text-[#EDE3CB]/85"}`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/contact" className="btn-primary mt-2 self-start">
                Check Availability <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pt-[72px]">{children}</main>

      {/* Footer */}
      <footer className="bg-[#0B0B0C] border-t border-white/5 mt-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-16 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <div className="font-display text-3xl tracking-wider text-[#EDE3CB]">JESSE J. ADAMS</div>
              <div className="section-label mt-2">IATSE Local 44 · Los Angeles Based Property Master</div>
              <p className="font-serif-accent italic text-lg text-[#EDE3CB]/75 mt-6 max-w-md leading-snug">
                Creative instincts, historical research, archive-level detail, and budget-minded department leadership for film and television.
              </p>
              <div className="brass-divider mt-8 max-w-md" />
              <div className="mt-6 text-sm text-[#EDE3CB]/75 space-y-2">
                <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 hover:text-[#B8935A] transition-colors">
                  <Mail size={14} className="text-[#B8935A]" /> {SITE.email}
                </a>
                <a href={`tel:${SITE.phone}`} className="flex items-center gap-3 hover:text-[#B8935A] transition-colors">
                  <Phone size={14} className="text-[#B8935A]" /> {SITE.phone}
                </a>
              </div>
            </div>

            <div>
              <div className="section-label">Site</div>
              <ul className="mt-4 space-y-2.5 text-sm">
                {NAV.slice(0, 5).map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="text-[#EDE3CB]/80 hover:text-[#B8935A] transition-colors">{n.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="section-label">More</div>
              <ul className="mt-4 space-y-2.5 text-sm">
                {NAV.slice(5).map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="text-[#EDE3CB]/80 hover:text-[#B8935A] transition-colors">{n.label}</Link>
                  </li>
                ))}
                <li><a href={SITE.imdb} target="_blank" rel="noreferrer" className="text-[#EDE3CB]/80 hover:text-[#B8935A] transition-colors">IMDb</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/5">
            <div className="section-label mb-3">Service Area</div>
            <p className="text-xs leading-relaxed text-[#EDE3CB]/55 max-w-4xl">
              Jesse J. Adams is a Los Angeles-based IATSE Local 44 Property Master available for feature films, streaming series, television, commercials, music videos, and additional photography units. Past credits include productions in Los Angeles, Atlanta, New York, New Orleans, and Chicago. Specializing in studio comedy, period recreation, archival research, custom prop fabrication, 3D printing and scanning, and graphics for film and television. Available for travel worldwide.
            </p>
          </div>

          <div className="mt-10 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between gap-3 text-xs text-[#EDE3CB]/55">
            <div>© {new Date().getFullYear()} Jesse J. Adams · Propmeister Art · All rights reserved.</div>
            <div>Los Angeles · Available worldwide</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
